import { api, edenFetch } from "@/lib/eden";

/**
 * Birthday Request FUNNEL data — the cross-surface growth actions (PRD 05 §6.5, D11). The cake
 * button files a request targeting a card message's author; anon-signer reconciliation attaches a
 * freshly-registered user's past anonymous signatures. Both require a session (called after the
 * responder/reader registers, or when already a member).
 */

/** POST /api/user/cake — file a birthday request for someone on a card. */
export interface CakeResult {
  birthday_request_id: string;
  contact_id: string;
  already?: boolean;
}

/** Server 422 detail when the person has no address on file and none was typed. */
export const CAKE_UNRESOLVABLE = "cake_target_unresolvable";

/**
 * A cake key (server lib/cake-target): `m:<messageId>` a note's author, `sender:<cardSendId>`
 * the card's sender, `r:<recipientId>` a recipient.
 */
export type CakeKey = string;
export const cakeKeyForMessage = (id: string): CakeKey => `m:${id}`;
export const cakeKeyForSender = (cardSendId: string): CakeKey => `sender:${cardSendId}`;
export const cakeKeyForRecipient = (id: string): CakeKey => `r:${id}`;

export const postCake = (
  target: CakeKey,
  surface: "read" | "sign",
  email?: string,
) =>
  edenFetch(
    api.api.user.cake.post({ target, surface, ...(email ? { email } : {}) }),
  ) as Promise<CakeResult>;

/** The viewer's state for one person (legacy `reminderStatus`). */
export interface CakeStatus {
  state: "ready" | "requested" | "has_birthday";
  birthday?: { month: number; day: number; year: number | null };
  /** False → no address on file; the dialog asks for one. */
  has_email: boolean;
  /** The viewer IS this person — no cake. */
  self: boolean;
}

export const cakeStatusKeys = {
  all: ["/api/user/cake/status"] as const,
  card: (cardSendId: string, recipientIds: string[]) =>
    ["/api/user/cake/status", cardSendId, [...recipientIds].sort().join(",")] as const,
};

/** GET /api/user/cake/status — every note author + the sender (+ the given recipients). */
export const fetchCakeStatuses = (cardSendId: string, recipientIds: string[]) =>
  edenFetch(
    api.api.user.cake.status.get({
      query: {
        cardSendId,
        ...(recipientIds.length ? { recipientIds: [...recipientIds].sort().join(",") } : {}),
      },
    }),
  ) as Promise<{ statuses: Record<CakeKey, CakeStatus> }>;

/** POST /api/sign/reconcile — attach this session's anon signatures/messages to the account.
 *  Idempotent (no-op when no anon_key cookie); safe to call once per fresh login. */
export const reconcileAnonSigner = () =>
  edenFetch(api.api.sign.reconcile.post({})) as Promise<{
    reconciled: boolean;
    messages: number;
    signerRows: number;
  }>;
